import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, TextComponent } from '@monorepo/react-components';
import { AnalyticsTabType, TransactionTabType } from '../../types';
import {
  getEmptyStateDescriptionKey,
  getEmptyStateTitleKey,
} from '../../utils';

import styles from './empty-state.module.scss';

interface EmptyStateProps {
  SvgComponent: React.LazyExoticComponent<React.ComponentType<any>>;
  title?: string;
  description?: string;
  tabType?: TransactionTabType | AnalyticsTabType;
  horizontal?: boolean;
  className?: string;
  iconClassName?: string;
}

const EmptyState = ({
  SvgComponent,
  title,
  description,
  tabType,
  horizontal = false,
  className = '',
  iconClassName = '',
}: EmptyStateProps) => {
  const { t } = useTranslation(['affiliate', 'common'] as const);

  // Then cast to string to bypass type check
  const titleText =
    title || (tabType ? t(getEmptyStateTitleKey(tabType) as any) : '');
  const descriptionText =
    description ||
    (tabType ? t(getEmptyStateDescriptionKey(tabType) as any) : '');

  // Determine layout class
  const containerClass = `${styles.emptyState} ${horizontal ? styles.horizontal : ''} ${className}`;
  const iconClass = `${styles.emptyStateIcon} ${iconClassName}`;

  const loadingFallback = (
    <TextComponent>{t('common:loading')}...</TextComponent>
  );

  const svgWithFallback = (
    <Suspense fallback={loadingFallback}>
      <SvgComponent className={iconClass} />
    </Suspense>
  );

  const textWithFallback = (
    <>
      <TextComponent level={3} weight={600} className={styles.emptyStateTitle}>
        {titleText}
      </TextComponent>
      <TextComponent level={6} className={styles.emptyStateDescription}>
        {descriptionText}
      </TextComponent>
    </>
  );

  // Conditional rendering based on layout
  if (horizontal) {
    return (
      <Flex className={containerClass}>
        <Flex
          vertical
          align="flex-start"
          justify="center"
          gap={8}
          className={styles.emptyStateContent}
        >
          {textWithFallback}
        </Flex>
        {svgWithFallback}
      </Flex>
    );
  }

  // Standard vertical layout
  return (
    <Flex vertical justify="center" align="center" className={containerClass}>
      {svgWithFallback}
      {textWithFallback}
    </Flex>
  );
};

export default EmptyState;
